import type { Conversation } from './types';

/**
 * The buckets the history list is sorted into.
 *
 * A flat list of thirty conversations, each with a timestamp under it, is a
 * wall of dates to read past. The buckets say which ones are recent without
 * making the reader compare every date to today themselves.
 */
export type HistoryGroupId = 'today' | 'yesterday' | 'lastWeek' | 'older';

export interface HistoryGroup {
  id: HistoryGroupId;
  conversations: Conversation[];
}

/** The order the groups are shown in, which is also the order of recency. */
export const HISTORY_GROUP_ORDER: HistoryGroupId[] = ['today', 'yesterday', 'lastWeek', 'older'];

/**
 * Midnight at the start of the local day `offsetDays` before `now`.
 *
 * Local rather than UTC on purpose: a conversation from 23:30 local time is
 * from today even when the stored timestamp lands on the next UTC day, and the
 * person reading the list is thinking in their own day.
 */
function startOfLocalDay(now: Date, offsetDays: number): number {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate() - offsetDays);
  return start.getTime();
}

/**
 * Which bucket a timestamp belongs to.
 *
 * A timestamp that cannot be parsed goes to the oldest bucket rather than
 * being dropped: it is still a conversation the user can open and delete.
 */
export function historyGroupId(when: Date, now: Date = new Date()): HistoryGroupId {
  const time = when.getTime();
  if (Number.isNaN(time)) return 'older';

  if (time >= startOfLocalDay(now, 0)) return 'today';
  if (time >= startOfLocalDay(now, 1)) return 'yesterday';
  // The last seven days, which includes today and yesterday.
  if (time >= startOfLocalDay(now, 7)) return 'lastWeek';
  return 'older';
}

/**
 * Sorts conversations into the buckets.
 *
 * The order within a bucket is the order it came in -- the server pages
 * newest first, and re-sorting here would only risk disagreeing with it. The
 * buckets themselves come back in recency order, so a list that arrives
 * out of order still reads from newest to oldest.
 */
export function groupConversations(
  conversations: Conversation[],
  now: Date = new Date(),
): HistoryGroup[] {
  const buckets = new Map<HistoryGroupId, Conversation[]>();

  for (const conversation of conversations) {
    const id = historyGroupId(new Date(conversation.updated_at), now);
    const bucket = buckets.get(id);
    if (bucket) {
      bucket.push(conversation);
    } else {
      buckets.set(id, [conversation]);
    }
  }

  return HISTORY_GROUP_ORDER.map((id) => ({
    id,
    conversations: buckets.get(id) ?? [],
  })).filter((group) => group.conversations.length > 0);
}
